/**
 * Modal para dejar reseña post-trade. Muestra estrellas clickeables y un
 * textarea opcional. Solo se puede dejar UNA reseña por trade — el backend
 * lo valida con UniqueConstraint, así que si ya dejaste una, vas a recibir
 * 409 Conflict.
 */
import { useState, type FormEvent } from "react";
import { Icon } from "../lib/Icon";
import { useToast } from "../lib/toast";
import { ApiError } from "../api/client";
import { createReview } from "../api/reviews";

interface Props {
  tradeProposalId: number;
  peerName: string;
  onClose: () => void;
  onDone: () => void;
}

export function ReviewModal({ tradeProposalId, peerName, onClose, onDone }: Props) {
  const [stars, setStars] = useState<number>(5);
  const [hover, setHover] = useState<number>(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createReview({
        trade_proposal_id: tradeProposalId,
        stars,
        text: text.trim() || undefined,
      });
      toast(`¡Reseña enviada a ${peerName}!`);
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("Ya dejaste una reseña para este trade.");
        onDone();
      } else {
        toast(`Error: ${e instanceof ApiError ? e.message : e}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const display = hover || stars;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 460, width: "100%", padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: 0 }}>
            Dejá una reseña
          </h2>
          <div style={{ flex: 1 }} />
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        <p style={{ color: "var(--fg-muted)", margin: 0, fontSize: 14 }}>
          ¿Cómo te fue intercambiando con <b>{peerName}</b>? Tu reseña ayuda a otros coleccionistas a confiar.
        </p>

        {/* Estrellas */}
        <div
          style={{ display: "flex", gap: 6, justifyContent: "center", padding: "8px 0" }}
          onMouseLeave={() => setHover(0)}
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= display;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setStars(n)}
                onMouseEnter={() => setHover(n)}
                aria-label={`${n} estrellas`}
                style={{
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  padding: 4,
                  color: filled ? "var(--accent-amber)" : "var(--border-strong)",
                  fontSize: 36,
                  lineHeight: 1,
                  transition: "transform 0.1s",
                  transform: filled && n === display ? "scale(1.15)" : "scale(1)",
                }}
              >
                ★
              </button>
            );
          })}
        </div>
        <div style={{ textAlign: "center", color: "var(--fg-muted)", fontSize: 12 }}>
          {display === 1 && "Pésima experiencia"}
          {display === 2 && "Hubo problemas"}
          {display === 3 && "Aceptable"}
          {display === 4 && "Buena experiencia"}
          {display === 5 && "Excelente — todo perfecto"}
        </div>

        {/* Comentario */}
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, color: "var(--fg-muted)" }}>
            Comentario (opcional)
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Ej: Súper puntual, las láminas vinieron impecables. ¡Recomendado!"
            style={{
              width: "100%",
              padding: "8px 10px",
              border: "1px solid var(--border)",
              borderRadius: 8,
              background: "var(--bg)",
              color: "var(--fg)",
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
          <div className="tiny muted" style={{ textAlign: "right", marginTop: 2 }}>
            {text.length} / 500
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Enviando…" : "Enviar reseña"}
          </button>
        </div>
      </form>
    </div>
  );
}
